// components/SimpleChatWidget.tsx
import React, { useState, useRef, useEffect } from 'react';
import { Send, X, MessageCircle, Sparkles } from 'lucide-react';
import { ChatMessage } from './ChatMessage';
import { ChatInput } from './ChatInput';
import { useChatbot } from '../hooks/useChatbot';
import { supabase } from '@/lib/supabaseClient';

interface SimpleChatWidgetProps {
  title?: string;
  welcomeMessage?: string;
  source?: string;
}

export const SimpleChatWidget: React.FC<SimpleChatWidgetProps> = ({ 
  title = "PlexiSystem AI",
  welcomeMessage = "Cześć! 👋 W czym mogę pomóc?",
  source = "app"
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [leadData, setLeadData] = useState<any>(null);
  const [awaitingLeadData, setAwaitingLeadData] = useState(false);
  const [theme, setTheme] = useState<any>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  const {
    messages,
    isLoading,
    sendMessage,
    startConversation,
    endConversation
  } = useChatbot({
    conversationSource: source,
    welcomeMessage
  });

  // Załaduj motyw
  useEffect(() => {
    const loadTheme = () => {
      const saved = localStorage.getItem('chatbot_theme');
      if (saved) {
        setTheme(JSON.parse(saved));
      }
    };
    
    loadTheme();
    
    // Nasłuchuj na zmiany motywu
    window.addEventListener('chatbot-theme-updated', loadTheme);
    return () => window.removeEventListener('chatbot-theme-updated', loadTheme);
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    // Automatycznie rozpocznij konwersację bez formularza
    if (isOpen && messages.length === 0) {
      startConversation();
    }
  }, [isOpen]);

  const handleSendMessage = async (content: string) => {
    // Sprawdź czy wiadomość zawiera dane kontaktowe
    const emailMatch = content.match(/\b[\w._%+-]+@[\w.-]+\.[A-Z|a-z]{2,}\b/i);
    const phoneMatch = content.match(/(?:\+48)?\s*(?:\d{3}[\s-]?\d{3}[\s-]?\d{3}|\d{2}[\s-]?\d{3}[\s-]?\d{2}[\s-]?\d{2})/);
    const nameMatch = content.match(/(?:nazywam się|jestem|moje imię|ja to)\s+([A-ZŁĄĆĘŃÓŚŹŻ][a-złąćęńóśźż]+(?:\s+[A-ZŁĄĆĘŃÓŚŹŻ][a-złąćęńóśźż]+)?)/i);
    
    if (emailMatch || phoneMatch) {
      // Utwórz lead z wykrytymi danymi
      const leadData = {
        email: emailMatch?.[0] || '',
        phone: phoneMatch?.[0]?.replace(/[\s-]/g, '') || '',
        name: nameMatch?.[1] || 'Klient czatu',
        company: '',
        message: `Zainteresowany: ${content}`
      };
      
      // Zapisz lead w bazie - tylko jeśli mamy email lub telefon
      if (leadData.email || leadData.phone) {
        try {
          const { data: lead, error } = await supabase
            .from('leads')
            .insert({
              contact_name: leadData.name,
              email: leadData.email || null,
              phone: leadData.phone || null,
              company_name: leadData.company || 'Nieznana',
              source: 'chatbot',
              notes: leadData.message,
              status: 'new'
            })
            .select()
            .single();
          
        if (!error && lead) {
          console.log('✅ Lead automatycznie zapisany:', lead);
          
          // Wyślij powiadomienie email
          try {
            await fetch('https://plexisystem-backend.onrender.com/api/send-email', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                to: 'lukasz@plexisystem.pl',
                subject: `🎯 Nowy lead z czatbota - ${leadData.name}`,
                html: `
                  <h2>Nowy lead z czatbota!</h2>
                  <p><strong>Imię:</strong> ${leadData.name}</p>
                  <p><strong>Email:</strong> ${leadData.email}</p>
                  <p><strong>Telefon:</strong> ${leadData.phone}</p>
                  <p><strong>Wiadomość:</strong> ${leadData.message}</p>
                  <hr>
                  <p>Zobacz w CRM: <a href="https://app.plexisystem.pl/crm/leads/${lead.id}">Otwórz lead</a></p>
                `
              })
            });
            console.log('📧 Email powiadomienie wysłane!');
          } catch (emailError) {
            console.error('Błąd wysyłania emaila:', emailError);
          }
        } else if (error) {
          console.error('Błąd zapisu leada:', error);
        }
      } catch (error) {
        console.error('Błąd zapisu leada:', error);
      }
      
      setLeadData(leadData);
      setAwaitingLeadData(false);
    }
  }
    
    await sendMessage(content);
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-20 right-6 text-white rounded-full p-4 shadow-2xl hover:shadow-orange-500/25 hover:scale-110 transition-all duration-300 group"
        style={{
          backgroundColor: theme?.primaryColor || '#f97316',
          borderRadius: theme?.borderRadius === 'none' ? '0' : 
                       theme?.borderRadius === 'small' ? '0.5rem' :
                       theme?.borderRadius === 'medium' ? '1rem' : '9999px',
          [theme?.position === 'bottom-left' ? 'left' : 'right']: '1.5rem',
          right: theme?.position === 'bottom-left' ? 'auto' : '1.5rem'
        }}
      >
        <MessageCircle className="w-6 h-6 group-hover:rotate-12 transition-transform" />
      </button>
    );
  }

  return (
    <div className="fixed bottom-20 right-6 bg-zinc-900 rounded-2xl shadow-2xl transition-all duration-300 border border-zinc-700 h-[500px] w-96 max-w-[calc(100vw-3rem)] z-[9999]">
      {/* Header */}
      <div className="bg-gradient-to-r from-zinc-800 to-zinc-700 text-white p-4 rounded-t-2xl flex justify-between items-center border-b border-zinc-600">
        <div className="flex items-center gap-3">
          <div className="relative">
            <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-400 rounded-full animate-pulse border-2 border-zinc-800"></div>
          </div>
          <div>
            <h3 className="font-semibold text-orange-400">{title}</h3>
            <p className="text-xs text-gray-400">Ceny orientacyjne • Oferta indywidualna</p>
          </div>
        </div>
        <button
          onClick={() => {
            setIsOpen(false);
            endConversation();
          }}
          className="hover:bg-zinc-600 p-1.5 rounded-lg transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 h-[calc(100%-8rem)] bg-zinc-950">
        {messages.map((message) => (
          <ChatMessage key={message.id} message={message} />
        ))}
        
        {isLoading && (
          <div className="flex items-center gap-2 p-3">
            <div className="w-8 h-8 bg-zinc-800 rounded-full flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-orange-500" />
            </div>
            <div className="bg-zinc-800 rounded-2xl px-4 py-2 flex gap-1">
              <div className="w-2 h-2 bg-orange-500 rounded-full animate-bounce"></div>
              <div className="w-2 h-2 bg-orange-500 rounded-full animate-bounce delay-100"></div>
              <div className="w-2 h-2 bg-orange-500 rounded-full animate-bounce delay-200"></div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <ChatInput 
        onSendMessage={handleSendMessage} 
        disabled={isLoading}
        placeholder="Napisz wiadomość..."
      />
    </div>
  );
};
